import {
  AttributesApiGetAttributesRequest,
  CategoryApiGetCategoriesRequest,
  CategoryApiGetNestedCategoriesRequest,
  ChatEntriesApiGetChatEntriesRequest,
  ConversionRuleStatus,
  DelayIndexApiGetDelayIndexPerCountryRequest,
  DelayIndexApiGetDelayIndexPerDiseaseRequest,
  DelayIndexApiGetDelayIndexRequest,
  DiseasesApiGetDiseasesRequest,
  PredicateLibraryGap,
  DiseasesApiGetDiseasesTrialsCountRequest,
  HistoryType,
  LocationApiGetLocationsRequest,
  PanelManagementApiGetPanelsRequest,
  PredicatePipelineApiTrialsRequest,
  PredicateQuestionsApiListRequest,
  PredicatesApiListRequest,
  QuestionsApiGetQuestionCategoriesRequest,
  QuestionsApiGetQuestionCriteriaRequest,
  QuestionsApiGetQuestionsRequest,
  QuestionsApiGetSuggestedQuestionsRequest,
  QuestionType,
  ReportingApiGetFunnelRequest,
  Role,
  SurveyManagementApiGetDescriptionTranslationsRequest,
  SurveyManagementApiGetResponsesRequest,
  SurveyManagementApiGetSurveyDetailsRequest,
  SurveyManagementApiGetSurveyQuestionsRequest,
  SurveyManagementApiGetSurveysRequest,
  SurveyManagementApiGetTitleTranslationsRequest,
  TeamApiGetTeamPanelsRequest,
  TrialManagementApiGetAcceptedRequest,
  TrialManagementApiGetEnrolledRequest,
  TrialManagementApiGetLeadSponsorsRequest,
  TrialManagementApiGetLocationsRequest,
  TrialManagementApiGetMatchesRequest,
  TrialManagementApiGetMilestonesRequest,
  TrialManagementApiGetPendingLocationChangesRequest,
  TrialManagementApiGetPublicTrialsRequest,
  TrialManagementApiGetTeamMandatesRequest,
  TrialManagementApiSearchRequest,
  UserPanelsApiGetMyPanelsRequest,
  UserSurveysApiGetMySurveyDetailRequest,
  UserSurveysApiGetMySurveysRequest,
  UserTrialsApiGetMyTrialLocationsRequest,
  UserTrialsApiGetMyTrialsRequest,
  UserTrialStatus,
} from '@data/sdk/match';
// Type-only on purpose: these two hook modules import this file's key factories back, so a
// value import here would be a runtime cycle.
import type {
  MatchedSetPage,
  ParityDivergenceFilters,
  ParityGroupedTrialFilters,
  ParitySliceFilters,
  ParityTrialFilters,
} from '../hooks/matching/parityReview';
import type { SliceReadinessFilters } from '../hooks/matching/sliceReadiness';
import {
  ConversionRuleFilters,
  DefaultQueryParams,
  DiseaseSearchParams,
  FunnelActivityQueryParams,
  ICDCodesSearchParams,
  ParityHistoryFilters,
  ParityRoutingFilters,
  PermissionsQueryParams,
  TrialVolunteerAnswersParams,
  UserTrialsQueryParams,
} from '../utils/types';

export const completionPercentageKeys = {
  all: () => ['completionPercentage'] as const,
  lists: () => [...completionPercentageKeys.all(), 'list'] as const,
};

export const attributesKeys = {
  all: () => ['attributes'] as const,
  lists: () => [...attributesKeys.all(), 'list'] as const,
  list: (filters: AttributesApiGetAttributesRequest) =>
    [...attributesKeys.lists(), { filters }] as const,
};

export const chatItemsKeys = {
  all: () => ['chatItems'] as const,
  allByHistoryType: (type: HistoryType) => [...chatItemsKeys.all(), type] as const,
  lists: (type: HistoryType) => [...chatItemsKeys.allByHistoryType(type), 'list'] as const,
  list: (type: HistoryType, filters: ChatEntriesApiGetChatEntriesRequest) =>
    [...chatItemsKeys.lists(type), { filters }] as const,
  details: () => [...chatItemsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...chatItemsKeys.details(), id] as const,
};

export const chatProgressKeys = {
  all: () => ['chatProgress'] as const,
  lists: () => [...chatProgressKeys.all(), 'list'] as const,
};

export const diseasesKeys = {
  all: () => ['diseases'] as const,
  lists: () => [...diseasesKeys.all(), 'list'] as const,
  list: (filters: DiseasesApiGetDiseasesRequest) => [...diseasesKeys.lists(), { filters }] as const,
  infiniteList: (filters: DiseasesApiGetDiseasesRequest) =>
    [...diseasesKeys.all(), 'infiniteList', { filters }] as const,
  trialsCount: (filters: DiseasesApiGetDiseasesTrialsCountRequest) =>
    [...diseasesKeys.lists(), 'trialsCount', { filters }] as const,
};

export const userTrialsKeys = {
  all: () => ['userTrials'] as const,
  allPerStatus: (status: UserTrialStatus) => [...userTrialsKeys.all(), status] as const,
  listPerStatus: (
    status: UserTrialStatus,
    filters: Pick<UserTrialsApiGetMyTrialsRequest, 'orderBy' | 'country'>,
  ) => [...userTrialsKeys.allPerStatus(status), { filters }] as const,
  lists: () => [...userTrialsKeys.all(), 'list'] as const,
  list: (filters: UserTrialsQueryParams) => [...userTrialsKeys.lists(), { filters }] as const,
  details: () => [...userTrialsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...userTrialsKeys.details(), id] as const,
  enrolledDetails: () => [...userTrialsKeys.all(), 'enrolledDetails'] as const,
  enrolledDetail: (trialId: string) => [...userTrialsKeys.enrolledDetails(), trialId] as const,
};

export const trialsKeys = {
  all: () => ['trials'] as const,
  allForTeam: (teamId?: string | null) =>
    [...trialsKeys.all(), ...(teamId ? [teamId] : [])] as const,
  list: (teamId?: string | null, filters?: TrialManagementApiSearchRequest['getTrialsBody']) =>
    [...trialsKeys.allForTeam(teamId), { filters }] as const,
  infiniteList: (
    teamId?: string | null,
    filters?: TrialManagementApiSearchRequest['getTrialsBody'],
  ) => [...trialsKeys.allForTeam(teamId), 'infinite', { filters }] as const,
  details: () => [...trialsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialsKeys.details(), id] as const,
};

export const publicTrialsKeys = {
  all: () => ['publicTrials'] as const,
  lists: () => [...publicTrialsKeys.all(), 'list'] as const,
  list: (filters?: TrialManagementApiGetPublicTrialsRequest) =>
    [...publicTrialsKeys.lists(), { filters }] as const,
};

export const trialCriteriaKeys = {
  all: () => ['trialCriteria'] as const,
  allForTrial: (id: string) => [...trialCriteriaKeys.all(), id] as const,
  lists: (id: string) => [...trialCriteriaKeys.allForTrial(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...trialCriteriaKeys.lists(id), { filters }] as const,
};

export const trialAtomicCriteriaKeys = {
  all: () => ['trialAtomicCriteria'] as const,
  detail: (trialId: string) => [...trialAtomicCriteriaKeys.all(), trialId] as const,
};

export const trialCriteriaAnswerCountsKeys = {
  all: () => ['trialCriteriaAnswerCounts'] as const,
  detail: (trialId: string) => [...trialCriteriaAnswerCountsKeys.all(), trialId] as const,
};

export const originalCriteriaKeys = {
  all: () => ['originalCriteria'] as const,
  allForTrial: (trialId: string) => [...originalCriteriaKeys.all(), trialId] as const,
  lists: (trialId: string) => [...originalCriteriaKeys.allForTrial(trialId), 'list'] as const,
  list: (trialId: string, splitStatus?: string) =>
    [...originalCriteriaKeys.lists(trialId), { splitStatus }] as const,
  details: () => [...originalCriteriaKeys.all(), 'detail'] as const,
  detail: (id: string) => [...originalCriteriaKeys.details(), id] as const,
  derivedPredicates: (id: string) => [...originalCriteriaKeys.detail(id), 'predicates'] as const,
};

export const conversionRulesKeys = {
  all: () => ['conversionRules'] as const,
  lists: () => [...conversionRulesKeys.all(), 'list'] as const,
  listByStatus: (status: ConversionRuleStatus) => [...conversionRulesKeys.lists(), status] as const,
  list: (status: ConversionRuleStatus, filters: ConversionRuleFilters) =>
    [...conversionRulesKeys.listByStatus(status), { filters }] as const,
};

export const parityRoutingKeys = {
  all: () => ['parityRouting'] as const,
  lists: () => [...parityRoutingKeys.all(), 'list'] as const,
  list: (filters: ParityRoutingFilters, page: { limit: number; offset: number }) =>
    [...parityRoutingKeys.lists(), { filters, page }] as const,
  histories: () => [...parityRoutingKeys.all(), 'history'] as const,
  history: (filters: ParityHistoryFilters, page: { limit: number; offset: number }) =>
    [...parityRoutingKeys.histories(), { filters, page }] as const,
  blastRadii: () => [...parityRoutingKeys.all(), 'blastRadius'] as const,
  blastRadius: (country: string, stage: string, icdCode?: string) =>
    [...parityRoutingKeys.blastRadii(), country, stage, icdCode ?? 'wildcard'] as const,
  countries: () => [...parityRoutingKeys.all(), 'countries'] as const,
};

export const sliceReadinessKeys = {
  all: () => ['sliceReadiness'] as const,
  slices: () => [...sliceReadinessKeys.all(), 'slices'] as const,
  sliceList: (filters: SliceReadinessFilters) =>
    [...sliceReadinessKeys.slices(), { filters }] as const,
  trials: () => [...sliceReadinessKeys.all(), 'trials'] as const,
  trialList: (icdCode: string, country: string) =>
    [...sliceReadinessKeys.trials(), icdCode, country] as const,
};

export const parityReviewKeys = {
  all: () => ['parityReview'] as const,
  slices: () => [...parityReviewKeys.all(), 'slices'] as const,
  sliceList: (filters: ParitySliceFilters) => [...parityReviewKeys.slices(), { filters }] as const,
  trials: () => [...parityReviewKeys.all(), 'trials'] as const,
  trialList: (filters: ParityTrialFilters) => [...parityReviewKeys.trials(), { filters }] as const,
  groupedTrials: () => [...parityReviewKeys.all(), 'groupedTrials'] as const,
  groupedTrialList: (filters: ParityGroupedTrialFilters) =>
    [...parityReviewKeys.groupedTrials(), { filters }] as const,
  footprints: () => [...parityReviewKeys.all(), 'footprint'] as const,
  footprint: (trialId: string) => [...parityReviewKeys.footprints(), trialId] as const,
  divergences: () => [...parityReviewKeys.all(), 'divergences'] as const,
  divergenceList: (trialId: string, filters: ParityDivergenceFilters) =>
    [...parityReviewKeys.divergences(), trialId, { filters }] as const,
  matchedSetSlices: () => [...parityReviewKeys.all(), 'matchedSetSlices'] as const,
  matchedSetSliceList: (page: MatchedSetPage) =>
    [...parityReviewKeys.matchedSetSlices(), { page }] as const,
  matchedSetSliceAll: () => [...parityReviewKeys.matchedSetSlices(), 'all'] as const,
  matchedSetTrials: () => [...parityReviewKeys.all(), 'matchedSetTrials'] as const,
  matchedSetTrialList: (slice: string, page: MatchedSetPage) =>
    [...parityReviewKeys.matchedSetTrials(), slice, { page }] as const,
  matchedSetPatients: () => [...parityReviewKeys.all(), 'matchedSetPatients'] as const,
  matchedSetPatientList: (trialId: string, slice: string, page: MatchedSetPage) =>
    [...parityReviewKeys.matchedSetPatients(), trialId, slice, { page }] as const,
};

/**
 * One key for the whole board: the screen reads every tier at once, so a create, rename,
 * reorder or drop all invalidate the same entry.
 */
export const tierKeys = {
  all: () => ['tiers'] as const,
  list: () => [...tierKeys.all(), 'list'] as const,
};

export const predicateLibraryItemKeys = {
  all: () => ['predicateLibraryItems'] as const,
  lists: () => [...predicateLibraryItemKeys.all(), 'list'] as const,
  list: (filters?: {
    canonicalType?: string;
    isDeprecated?: boolean;
    limit?: number;
    offset?: number;
    search?: string;
  }) => [...predicateLibraryItemKeys.lists(), { filters }] as const,
  /** Kept apart from `list`: the pages accumulate under one key, so the shapes differ. */
  infiniteList: (filters?: { isDeprecated?: boolean; search?: string }) =>
    [...predicateLibraryItemKeys.lists(), 'infinite', { filters }] as const,
  details: () => [...predicateLibraryItemKeys.all(), 'detail'] as const,
  detail: (id: string) => [...predicateLibraryItemKeys.details(), id] as const,
};

/** The cross-trial queues of predicates blocked on a missing library entry, one per gap. */
export const predicateLibraryQueueKeys = {
  all: () => ['predicateLibraryQueue'] as const,
  list: (filters: {
    gap: PredicateLibraryGap;
    limit?: number;
    offset?: number;
    predicateLibraryItemId?: string;
    search?: string;
  }) => [...predicateLibraryQueueKeys.all(), { filters }] as const,
};

export const predicateKeys = {
  all: () => ['predicates'] as const,
  lists: () => [...predicateKeys.all(), 'list'] as const,
  list: (filters: PredicatesApiListRequest) => [...predicateKeys.lists(), { filters }] as const,
  details: () => [...predicateKeys.all(), 'detail'] as const,
  detail: (id: string) => [...predicateKeys.details(), id] as const,
};

export const pipelineTrialKeys = {
  all: () => ['pipelineTrials'] as const,
  lists: () => [...pipelineTrialKeys.all(), 'list'] as const,
  list: (filters: PredicatePipelineApiTrialsRequest) =>
    [...pipelineTrialKeys.lists(), { filters }] as const,
  steps: (trialId: string) => [...pipelineTrialKeys.all(), 'steps', trialId] as const,
  /** Trial-independent: the auto-approve switches are pipeline-wide settings. */
  autoApprove: () => [...pipelineTrialKeys.all(), 'autoApprove'] as const,
};

export const predicateQuestionKeys = {
  all: () => ['predicateQuestions'] as const,
  lists: () => [...predicateQuestionKeys.all(), 'list'] as const,
  list: (filters: PredicateQuestionsApiListRequest) =>
    [...predicateQuestionKeys.lists(), { filters }] as const,
  /** Kept apart from `list`: the pages accumulate under one key, so the shapes differ. */
  infiniteList: (filters: PredicateQuestionsApiListRequest) =>
    [...predicateQuestionKeys.lists(), 'infinite', { filters }] as const,
  details: () => [...predicateQuestionKeys.all(), 'detail'] as const,
  detail: (id: string) => [...predicateQuestionKeys.details(), id] as const,
};

export const projectionKeys = {
  all: () => ['projections'] as const,
  lists: () => [...projectionKeys.all(), 'list'] as const,
  list: (filters?: { isDeprecated?: boolean; predicateLibraryItemId?: string }) =>
    [...projectionKeys.lists(), { filters }] as const,
};

export const cohortKeys = {
  all: () => ['cohorts'] as const,
  allForTrial: (trialId: string) => [...cohortKeys.all(), trialId] as const,
  list: (trialId: string) => [...cohortKeys.allForTrial(trialId), 'list'] as const,
  detail: (id: string) => [...cohortKeys.all(), 'detail', id] as const,
};

export const criteriaActivityKeys = {
  all: () => ['criteriaActivity'] as const,
  allForCriteria: (id: string) => [...criteriaActivityKeys.all(), id] as const,
  lists: (id: string) => [...criteriaActivityKeys.allForCriteria(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...criteriaActivityKeys.lists(id), { filters }] as const,
};

export const tokenDataKeys = {
  all: () => ['tokenData'] as const,
  lists: () => [...tokenDataKeys.all(), 'list'] as const,
};

export const configurationsKeys = {
  all: () => ['configurations'] as const,
  maintenance: () => [...configurationsKeys.all(), 'maintenance'] as const,
  questions: () => [...configurationsKeys.all(), 'questions'] as const,
  tags: () => [...configurationsKeys.all(), 'tags'] as const,
};

export const insightsConfigKeys = {
  all: () => ['insightsConfig'] as const,
};

export const criteriaQuestionsKeys = {
  all: () => ['criteriaQuestions'] as const,
  allForType: (types?: QuestionType[]) => [...criteriaQuestionsKeys.all(), types] as const,
  lists: (types?: QuestionType[]) => [...criteriaQuestionsKeys.allForType(types), 'list'] as const,
  list: (filters: QuestionsApiGetQuestionsRequest) =>
    [...criteriaQuestionsKeys.lists(filters.getQuestionsBody.questionTypes), { filters }] as const,
  details: () => [...criteriaQuestionsKeys.all(), 'detail'] as const,
  detail: (questionId: string) => [...criteriaQuestionsKeys.details(), questionId] as const,
};

export const trialActivityKeys = {
  all: () => ['trialActivity'] as const,
  lists: () => [...trialActivityKeys.all(), 'list'] as const,
  list: (id: string) => [...trialActivityKeys.lists(), id] as const,
};

export const userTrialContactsKeys = {
  all: () => ['userTrialContacts'] as const,
  lists: () => [...userTrialContactsKeys.all(), 'list'] as const,
  list: (id: string) => [...userTrialContactsKeys.lists(), id] as const,
  currentFlow: () => [...userTrialContactsKeys.all(), 'currentFlow'] as const,
};

export const trialContactsKeys = {
  all: () => ['trialContacts'] as const,
  lists: () => [...trialContactsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialContactsKeys.lists(), trialId] as const,
  details: () => [...trialContactsKeys.all(), 'detail'] as const,
  detail: (contactId: string) => [...trialContactsKeys.details(), contactId] as const,
  contactOptions: (trialId: string) =>
    [...trialContactsKeys.all(), 'contactOptions', trialId] as const,
};

export const trialLocationsKeys = {
  all: () => ['trialLocations'] as const,
  lists: () => [...trialLocationsKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetLocationsRequest) =>
    [...trialLocationsKeys.lists(), { filters }] as const,
  // Under the trial's list key, so each mutation that refreshes the list refreshes the countries.
  countries: (trialId: string) =>
    [...trialLocationsKeys.list({ id: trialId }), 'countries'] as const,
  details: () => [...trialLocationsKeys.all(), 'detail'] as const,
  detail: (locationId: string) => [...trialLocationsKeys.details(), locationId] as const,
};

export const questionTranslationsKeys = {
  all: () => ['questionTranslations'] as const,
  lists: () => [...questionTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...questionTranslationsKeys.lists(), id] as const,
};

export const questionContextTranslationsKeys = {
  all: () => ['questionContextTranslations'] as const,
  lists: () => [...questionContextTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...questionContextTranslationsKeys.lists(), id] as const,
};

export const questionDependenciesKeys = {
  all: () => ['questionDependencies'] as const,
  lists: () => [...questionDependenciesKeys.all(), 'list'] as const,
  list: (id: string) => [...questionDependenciesKeys.lists(), id] as const,
  details: () => [...questionDependenciesKeys.all(), 'detail'] as const,
  detail: (dependencyId: string) => [...questionDependenciesKeys.details(), dependencyId] as const,
};

export const dependencyProposalKeys = {
  all: () => ['dependencyProposals'] as const,
  lists: () => [...dependencyProposalKeys.all(), 'list'] as const,
  list: (questionId: string) => [...dependencyProposalKeys.lists(), questionId] as const,
};

export const similarQuestionsKeys = {
  all: () => ['similarQuestions'] as const,
  lists: () => [...similarQuestionsKeys.all(), 'list'] as const,
  list: (filters: DefaultQueryParams) => [...similarQuestionsKeys.lists(), { filters }] as const,
  details: () => [...similarQuestionsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...similarQuestionsKeys.details(), id] as const,
};

export const suggestedQuestionsKeys = {
  all: () => ['suggestedQuestions'] as const,
  lists: () => [...suggestedQuestionsKeys.all(), 'list'] as const,
  list: (filters: QuestionsApiGetSuggestedQuestionsRequest) =>
    [...suggestedQuestionsKeys.lists(), { filters }] as const,
};

export const trialTitleTranslationKeys = {
  all: () => ['trialTitleTranslation'] as const,
  lists: () => [...trialTitleTranslationKeys.all(), 'list'] as const,
  list: (id: string) => [...trialTitleTranslationKeys.lists(), id] as const,
};

export const trialTeamsKeys = {
  all: () => ['trialTeams'] as const,
  lists: () => [...trialTeamsKeys.all(), 'list'] as const,
  list: (id: string) => [...trialTeamsKeys.lists(), id] as const,
};

export const trialSummaryTranslationKeys = {
  all: () => ['trialSummaryTranslation'] as const,
  lists: () => [...trialSummaryTranslationKeys.all(), 'list'] as const,
  list: (id: string) => [...trialSummaryTranslationKeys.lists(), id] as const,
};

export const icdCodesKeys = {
  all: () => ['icdCodes'] as const,
  lists: () => [...icdCodesKeys.all(), 'list'] as const,
  list: (filters: ICDCodesSearchParams) => [...icdCodesKeys.lists(), { filters }] as const,
};

export const volunteerOverviewKeys = {
  all: () => ['volunteerOverview'] as const,
  lists: () => [...volunteerOverviewKeys.all(), 'list'] as const,
  list: (id: string) => [...volunteerOverviewKeys.lists(), id] as const,
  listByCountry: (id: string, locationId?: string, cohortId?: string) =>
    [...volunteerOverviewKeys.list(id), locationId, cohortId] as const,
};

export const volunteerAnswersKeys = {
  all: () => ['volunteerAnswers'] as const,
  lists: () => [...volunteerAnswersKeys.all(), 'list'] as const,
  allForTrial: (id: string) => [...volunteerAnswersKeys.all(), id] as const,
  list: (id: string, filters: TrialVolunteerAnswersParams) =>
    [...volunteerAnswersKeys.allForTrial(id), { filters }] as const,
  details: () => [...volunteerAnswersKeys.all(), 'detail'] as const,
  detail: (id: string) => [...volunteerAnswersKeys.details(), id] as const,
};

export const matchedVolunteersKeys = {
  all: () => ['matchedVolunteers'] as const,
  allForTrial: (id: string) => [...matchedVolunteersKeys.all(), id] as const,
  lists: (id: string) => [...matchedVolunteersKeys.allForTrial(id), 'list'] as const,
  list: ({ id, ...params }: TrialManagementApiGetMatchesRequest) =>
    [...matchedVolunteersKeys.lists(id), { params }] as const,
};

export const enrolledPatientsKeys = {
  all: () => ['enrolledPatients'] as const,
  allForTrial: (id: string) => [...enrolledPatientsKeys.all(), id] as const,
  lists: (id: string) => [...enrolledPatientsKeys.allForTrial(id), 'list'] as const,
  list: ({ id, ...params }: TrialManagementApiGetEnrolledRequest) =>
    [...enrolledPatientsKeys.lists(id), { params }] as const,
};

export const acceptedPatientsKeys = {
  all: () => ['acceptedPatients'] as const,
  allForTrial: (id: string) => [...acceptedPatientsKeys.all(), id] as const,
  lists: (id: string) => [...acceptedPatientsKeys.allForTrial(id), 'list'] as const,
  list: ({ id, ...params }: TrialManagementApiGetAcceptedRequest) =>
    [...acceptedPatientsKeys.lists(id), { params }] as const,
};
export const volunteerContactsKeys = {
  all: () => ['volunteerContacts'] as const,
  lists: () => [...volunteerContactsKeys.all(), 'list'] as const,
  list: (id: string) => [...volunteerContactsKeys.lists(), id] as const,
};

export const funnelActivityKeys = {
  all: () => ['funnelActivity'] as const,
  lists: () => [...funnelActivityKeys.all(), 'list'] as const,
  list: (filters: FunnelActivityQueryParams) =>
    [...funnelActivityKeys.lists(), { filters }] as const,
  details: () => [...funnelActivityKeys.all(), 'detail'] as const,
  detail: (id: string) => [...funnelActivityKeys.details(), id] as const,
};

export const funnelActivityUsersKeys = {
  all: () => ['funnelActivityUsers'] as const,
  lists: () => [...funnelActivityUsersKeys.all(), 'list'] as const,
  list: (params: DefaultQueryParams) => [...funnelActivityUsersKeys.lists(), { params }] as const,
};

export const trialPermissionsKeys = {
  all: () => ['trialPermissions'] as const,
  lists: () => [...trialPermissionsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialPermissionsKeys.lists(), trialId] as const,
  details: () => [...trialPermissionsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialPermissionsKeys.details(), id] as const,
};

export const trialAccessRequestsKeys = {
  all: () => ['trialAccessRequests'] as const,
  lists: () => [...trialAccessRequestsKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialAccessRequestsKeys.lists(), trialId] as const,
  details: () => [...trialAccessRequestsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialAccessRequestsKeys.details(), id] as const,
};

export const userTrialPermissionsKeys = {
  all: () => ['userTrialPermissions'] as const,
  lists: () => [...userTrialPermissionsKeys.all(), 'list'] as const,
  list: (id: string) => [...userTrialPermissionsKeys.lists(), id] as const,
};

export const trialInvitesKeys = {
  all: () => ['trialInvites'] as const,
  lists: () => [...trialInvitesKeys.all(), 'list'] as const,
  list: (trialId: string) => [...trialInvitesKeys.lists(), trialId] as const,
  details: () => [...trialInvitesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialInvitesKeys.details(), id] as const,
};

export const volunteerTrialOverviewKeys = {
  all: () => ['volunteerTrialOverview'] as const,
  lists: () => [...volunteerTrialOverviewKeys.all(), 'list'] as const,
};

export const hospitalsKeys = {
  all: () => ['hospitals'] as const,
  lists: () => [...hospitalsKeys.all(), 'list'] as const,
  list: (filters: DiseaseSearchParams) => [...hospitalsKeys.lists(), { filters }] as const,
};

export const subscriptionPreferencesKeys = {
  all: () => ['subscriptionPreferences'] as const,
  lists: () => [...subscriptionPreferencesKeys.all(), 'list'] as const,
  list: (id: string) => [...subscriptionPreferencesKeys.lists(), id] as const,
};

export const consentsKeys = {
  all: () => ['consents'] as const,
  lists: () => [...consentsKeys.all(), 'list'] as const,
  list: () => [...consentsKeys.lists()] as const,
};

export const notificationsKeys = {
  all: () => ['notifications'] as const,
  lists: () => [...notificationsKeys.all(), 'list'] as const,
  list: () => [...notificationsKeys.lists()] as const,
};

export const unreadNotificationsKeys = {
  all: () => ['unreadNotifications'] as const,
};

export const icdCodeTranslationsKeys = {
  all: () => ['icdCodeTranslations'] as const,
  lists: () => [...icdCodeTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...icdCodeTranslationsKeys.lists(), id] as const,
};

export const icdCodeNamesTranslationsKeys = {
  all: () => ['icdCodeNamesTranslations'] as const,
  lists: () => [...icdCodeNamesTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...icdCodeNamesTranslationsKeys.lists(), id] as const,
  detail: (id: string, filters: DiseaseSearchParams) =>
    [...icdCodeNamesTranslationsKeys.list(id), { filters }] as const,
};

export const hospitalCentersKeys = {
  all: () => ['hospitalCenters'] as const,
  lists: () => [...hospitalCentersKeys.all(), 'list'] as const,
  list: (filters: ICDCodesSearchParams) => [...hospitalCentersKeys.lists(), { filters }] as const,
};

export const hospitalCenterTranslationsKeys = {
  all: () => ['hospitalCenterTranslations'] as const,
  lists: () => [...hospitalCenterTranslationsKeys.all(), 'list'] as const,
  list: (id: string) => [...hospitalCenterTranslationsKeys.lists(), id] as const,
};

export const hospitalCenterNamesKeys = {
  all: () => ['hospitalCenterNames'] as const,
  allForHospital: (id: string) => [...hospitalCenterNamesKeys.all(), id] as const,
  lists: (id: string) => [...hospitalCenterNamesKeys.allForHospital(id), 'list'] as const,
  list: (id: string, filters: DiseaseSearchParams) =>
    [...hospitalCenterNamesKeys.lists(id), { filters }] as const,
  details: () => [...hospitalCenterNamesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...hospitalCenterNamesKeys.details(), id] as const,
};

export const hospitalCenterDetailsKeys = {
  all: () => ['hospitalCenterDetails'] as const,
  lists: () => [...hospitalCenterDetailsKeys.all(), 'list'] as const,
  list: (id: string) => [...hospitalCenterDetailsKeys.lists(), id] as const,
};

export const leadSponsorKeys = {
  all: () => ['leadSponsor'] as const,
  lists: () => [...leadSponsorKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetLeadSponsorsRequest) =>
    [...leadSponsorKeys.lists(), { filters }] as const,
};

export const userTrialLocationsKeys = {
  all: () => ['userTrialLocations'] as const,
  lists: () => [...userTrialLocationsKeys.all(), 'list'] as const,
  list: (filters: UserTrialsApiGetMyTrialLocationsRequest) =>
    [...userTrialLocationsKeys.lists(), { filters }] as const,
  details: () => [...userTrialLocationsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...userTrialLocationsKeys.details(), id] as const,
};

export const invitesKeys = {
  all: () => ['invites'] as const,
  allForRole: (role?: Role) => [...invitesKeys.all(), role] as const,
  lists: (role: Role) => [...invitesKeys.allForRole(role), 'list'] as const,
  list: (role: Role, params: PermissionsQueryParams) =>
    [...invitesKeys.lists(role), { params }] as const,
};

export const publicTrialLocationsKeys = {
  all: (id: string) => ['publicTrialLocations', id] as const,
  lists: (id: string) => [...publicTrialLocationsKeys.all(id), 'list'] as const,
  list: (id: string, filters: DefaultQueryParams) =>
    [...publicTrialLocationsKeys.lists(id), { filters }] as const,
};

export const locationsKeys = {
  all: () => ['locations'] as const,
  lists: () => [...locationsKeys.all(), 'list'] as const,
  list: (filters: LocationApiGetLocationsRequest) =>
    [...locationsKeys.lists(), { filters }] as const,
};

export const teamMembersKeys = {
  all: () => ['teamMembers'] as const,
  lists: () => [...teamMembersKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamMembersKeys.lists(), teamId] as const,
  listCount: (teamId: string) => [...teamMembersKeys.lists(), 'count', teamId] as const,
};

export const teamInvitesKeys = {
  all: () => ['teamInvites'] as const,
  lists: () => [...teamInvitesKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamInvitesKeys.lists(), teamId] as const,
};

export const teamsKeys = {
  all: () => ['teams'] as const,
  lists: () => [...teamsKeys.all(), 'list'] as const,
  list: (teamId: string) => [...teamsKeys.lists(), teamId] as const,
  details: () => [...teamsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...teamsKeys.details(), id] as const,
  panels: (params: TeamApiGetTeamPanelsRequest) =>
    [...teamsKeys.all(), 'panels', { params }] as const,
};

export const metadataKeys = {
  all: () => ['metadata'] as const,
};

export const questionOptionsKeys = {
  all: () => ['questionOptions'] as const,
  lists: () => [...questionOptionsKeys.all(), 'list'] as const,
  list: (id: string) => [...questionOptionsKeys.lists(), id] as const,
  details: () => [...questionOptionsKeys.all(), 'detail'] as const,
  detail: (optionId: string) => [...questionOptionsKeys.details(), optionId] as const,
};

export const billingKeys = {
  all: () => ['billing'] as const,
  checkoutSession: (teamId: string) => [...billingKeys.all(), 'checkoutSession', teamId],
  customerPortalURL: (teamId: string) => [...billingKeys.all(), 'customerPortalURL', teamId],
};

export const teamMandatesKeys = {
  all: () => ['teamMandates'] as const,
  lists: () => [...teamMandatesKeys.all(), 'list'] as const,
  list: (filters: TrialManagementApiGetTeamMandatesRequest) =>
    [...teamMandatesKeys.lists(), { filters }] as const,
};

export const categoriesKeys = {
  all: () => ['categories'] as const,
  lists: () => [...categoriesKeys.all(), 'list'] as const,
  list: (filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.lists(), { filters }] as const,
  questionList: (params: QuestionsApiGetQuestionCategoriesRequest) =>
    [...categoriesKeys.all(), 'questionList', params] as const,
  question: (id: string) => [...categoriesKeys.all(), 'question', id] as const,
  categories: (filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.all(), 'categories', { filters }] as const,
  trials: (id: string, filters: CategoryApiGetCategoriesRequest) =>
    [...categoriesKeys.question(id), 'trials', { filters }] as const,
  nested: (filters: CategoryApiGetNestedCategoriesRequest) =>
    [...categoriesKeys.all(), 'nested', { filters }] as const,
};

export const questionCriteriaKeys = {
  all: () => ['questionCriteria'] as const,
  lists: () => [...questionCriteriaKeys.all(), 'list'] as const,
  list: ({ id, ...params }: QuestionsApiGetQuestionCriteriaRequest) =>
    [...questionCriteriaKeys.lists(), { id, ...params }] as const,
};

export const usersKeys = {
  all: () => ['users'] as const,
  admins: () => [...usersKeys.all(), 'admins'] as const,
};

export const teamTrialsKeys = {
  all: () => ['teamTrials'] as const,
  list: (teamId: string) => [...teamTrialsKeys.all(), teamId] as const,
};

export const delayIndexKeys = {
  all: () => ['delayIndex'] as const,
  details: (params: DelayIndexApiGetDelayIndexRequest) =>
    [...delayIndexKeys.all(), { params }] as const,
  detailsPerDisease: (params: DelayIndexApiGetDelayIndexPerDiseaseRequest) =>
    [...delayIndexKeys.all(), 'detailsPerDisease', { params }] as const,
  detailsPerCountry: (params: DelayIndexApiGetDelayIndexPerCountryRequest) =>
    [...delayIndexKeys.all(), 'detailsPerCountry', { params }] as const,
};

export const surveysKeys = {
  all: () => ['surveys'] as const,
  list: (params: SurveyManagementApiGetSurveysRequest) =>
    [...surveysKeys.all(), { params }] as const,
  responses: (params: SurveyManagementApiGetResponsesRequest) =>
    [...surveysKeys.all(), 'responses', { params }] as const,
  details: (params: SurveyManagementApiGetSurveyDetailsRequest) =>
    [...surveysKeys.all(), 'details', { params }] as const,
  questions: (params: SurveyManagementApiGetSurveyQuestionsRequest) =>
    [...surveysKeys.all(), 'questions', { params }] as const,
  questionsInfinite: (params: SurveyManagementApiGetSurveyQuestionsRequest) =>
    [...surveysKeys.all(), 'questionsInfinite', { params }] as const,
  titleTranslations: (params: SurveyManagementApiGetTitleTranslationsRequest) =>
    [...surveysKeys.all(), 'titleTranslations', { params }] as const,
  descriptionTranslations: (params: SurveyManagementApiGetDescriptionTranslationsRequest) =>
    [...surveysKeys.all(), 'descriptionTranslations', { params }] as const,
};

export const userSurveysKeys = {
  all: () => ['userSurveys'] as const,
  lists: () => [...userSurveysKeys.all(), 'list'] as const,
  list: (params: UserSurveysApiGetMySurveysRequest) =>
    [...userSurveysKeys.lists(), { params }] as const,
  overview: () => [...userSurveysKeys.all(), 'overview'] as const,
  details: () => [...userSurveysKeys.all(), 'details'] as const,
  detail: (params: UserSurveysApiGetMySurveyDetailRequest) =>
    [...userSurveysKeys.details(), { params }] as const,
};

export const panelKeys = {
  all: ['panels'] as const,
  lists: () => [...panelKeys.all, 'list'] as const,
  list: (params: PanelManagementApiGetPanelsRequest) => [...panelKeys.lists(), params] as const,
  details: () => [...panelKeys.all, 'detail'] as const,
  detail: (id: string) => [...panelKeys.details(), id] as const,
};

export const userPanelsKeys = {
  all: () => ['userPanels'] as const,
  lists: () => [...userPanelsKeys.all(), 'list'] as const,
  list: (params: UserPanelsApiGetMyPanelsRequest) =>
    [...userPanelsKeys.lists(), { params }] as const,
  details: () => [...userPanelsKeys.all(), 'detail'] as const,
  detail: (id: string) => [...userPanelsKeys.details(), id] as const,
};

export const trialMilestonesKeys = {
  all: () => ['trialMilestones'] as const,
  lists: () => [...trialMilestonesKeys.all(), 'list'] as const,
  list: (params: TrialManagementApiGetMilestonesRequest) =>
    [...trialMilestonesKeys.lists(), { params }] as const,
  details: () => [...trialMilestonesKeys.all(), 'detail'] as const,
  detail: (id: string) => [...trialMilestonesKeys.details(), id] as const,
  links: () => [...trialMilestonesKeys.all(), 'links'] as const,
  link: (id: string) => [...trialMilestonesKeys.links(), id] as const,
  translations: () => [...trialMilestonesKeys.all(), 'translations'] as const,
  translation: (id: string) => [...trialMilestonesKeys.translations(), id] as const,
};

export const pendingLocationChangesKeys = {
  all: () => ['pendingLocationChanges'] as const,
  lists: () => [...pendingLocationChangesKeys.all(), 'list'] as const,
  list: (params: TrialManagementApiGetPendingLocationChangesRequest) =>
    [...pendingLocationChangesKeys.lists(), { params }] as const,
};

export const linkedAccountsKeys = {
  all: () => ['linkedAccounts'] as const,
};

// Both endpoints are scoped server-side to whatever team the caller's token carries — teamId
// has to be part of the key, or switching teams shows the previous team's cached figures while
// the new team's request is in flight (TanStack Query returns cached data with isFetching=true
// before the new response lands).
export const reportingKeys = {
  all: () => ['reporting'] as const,
  // `asTeamId` (the admin "view as" override) has to sit alongside `teamId` in every key below —
  // an admin's own teamId never changes while they switch which team they're viewing, so
  // without it, switching teams in the view-as selector would keep showing the previous
  // team's cached figures.
  userOverview: (teamId?: string, asTeamId?: string) =>
    [...reportingKeys.all(), 'userOverview', teamId, asTeamId] as const,
  funnels: (teamId?: string, asTeamId?: string) =>
    [...reportingKeys.all(), 'funnel', teamId, asTeamId] as const,
  funnel: (
    teamId: string | undefined,
    asTeamId: string | undefined,
    params: ReportingApiGetFunnelRequest,
  ) => [...reportingKeys.funnels(teamId, asTeamId), params] as const,
  userSources: () => [...reportingKeys.all(), 'userSources'] as const,
  // Tiered (free/paid) endpoints: the server resolves the tier from the token's team, so the
  // key carries teamId and no "view as" override. The trial endpoints also carry the slicers.
  trialOverview: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'trialOverview', teamId, filters] as const,
  trialGeography: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'trialGeography', teamId, filters] as const,
  trialCriteria: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'trialCriteria', teamId, filters] as const,
  trialDelays: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'trialDelays', teamId, filters] as const,
  trialRejections: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'trialRejections', teamId, filters] as const,
  siteOpportunity: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'siteOpportunity', teamId, filters] as const,
  chatbot: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'chatbot', teamId, filters] as const,
  userFunnel: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'userFunnel', teamId, filters] as const,
  patientOverview: (teamId?: string, filters?: object) =>
    [...reportingKeys.all(), 'patientOverview', teamId, filters] as const,
  patientGeography: (teamId?: string) =>
    [...reportingKeys.all(), 'patientGeography', teamId] as const,
  whatIfTrials: (teamId?: string, params?: object) =>
    [...reportingKeys.all(), 'whatIfTrials', teamId, params] as const,
  whatIf: (teamId?: string, scenario?: object) =>
    [...reportingKeys.all(), 'whatIf', teamId, scenario] as const,
  // The admin "view as" value sits in the key: an admin's teamId never changes while they
  // switch teams or profiles, so without it the previous one's figures would stay on screen.
  landing: (teamId?: string, viewAs?: string) =>
    [...reportingKeys.all(), 'landing', teamId, viewAs] as const,
};

export const teamReportingScopeKeys = {
  all: () => ['teamReportingScope'] as const,
  scope: (teamId: string) => [...teamReportingScopeKeys.all(), teamId] as const,
  auditLogs: (teamId: string) => [...teamReportingScopeKeys.all(), teamId, 'auditLog'] as const,
  auditLog: (teamId: string, page: { limit: number; offset: number }) =>
    [...teamReportingScopeKeys.auditLogs(teamId), page] as const,
};

export const recordSharesKeys = {
  all: () => ['recordShares'] as const,
  capability: () => [...recordSharesKeys.all(), 'capability'] as const,
  providers: () => [...recordSharesKeys.all(), 'providers'] as const,
  details: () => [...recordSharesKeys.all(), 'detail'] as const,
  detail: (shareId: string) => [...recordSharesKeys.details(), shareId] as const,
};
